// `listSearch` — the ONE client-side full-text list filter
// (wiki/feature-list-search.md). It narrows the rows a page already holds; it
// issues no request and touches no store. A page supplies its rows and one
// `searchText` function; that function is the only per-list code there is.

import { activeCulture } from '$lib/i18n';

/** Trailing-edge debounce: the box updates per keystroke, the results 300ms after the last. */
const DEBOUNCE_MS = 300;

export interface ListSearch<T> {
  /** What the box shows — updates on every keystroke. */
  readonly query: string;
  /** The filtered rows — updates on the debounce. */
  readonly results: T[];
  /** A non-empty query is applied. */
  readonly active: boolean;
  setQuery(next: string): void;
  clear(): void;
}

/**
 * Case-folded, diacritic-folded text: NFD splits an accented letter into its
 * base plus combining marks, and the marks are dropped — so unaccented typing
 * matches accented text on both sides of the comparison.
 *
 * `đ`/`Đ` is folded explicitly: a stroke letter carries no combining mark, so
 * NFD leaves it standing, and Vietnamese — a shipped UI language in which the
 * letter is common — would otherwise be unreachable by unaccented typing.
 */
export function normalizeSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[đĐ]/g, 'd')
    .toLowerCase();
}

export function listSearch<T>(rows: () => T[], searchText: (row: T) => string): ListSearch<T> {
  let query = $state('');
  let applied = $state('');
  let timer: ReturnType<typeof setTimeout> | null = null;

  // PLAIN WeakMap, deliberately: nothing renders off it, and a reactive cache
  // written by the $derived that reads it would re-trigger itself. Keyed by the
  // row OBJECT — unchanged rows keep their identity across a push
  // (wiki/feature-frontend-cqrs-dataflow.md §"A push re-renders one row"), so a
  // push touching one row recomputes one row's text, and a row that never
  // returns is collected with its entry.
  //
  // The MAP ITSELF is derived on the active culture. Every `searchText` a page
  // supplies is localized — it resolves through `t()` — and `setCulture` swaps
  // the catalogue in place without a reload, which changes no row's identity
  // and would therefore invalidate nothing. A culture change yields a fresh
  // empty map, so every row re-normalizes into the new language on next read;
  // within one culture the same map comes back and the per-row memo holds.
  const cache = $derived.by(() => {
    activeCulture();
    return new WeakMap<object, string>();
  });

  function textOf(row: T): string {
    const key = row as object;
    let text = cache.get(key);
    if (text === undefined) {
      text = normalizeSearchText(searchText(row));
      cache.set(key, text);
    }
    return text;
  }

  // Normalized + tokenized ONCE per applied query, never per row.
  const tokens = $derived(normalizeSearchText(applied).split(/\s+/).filter(Boolean));

  // A $derived over the debounced query and the rows — it never writes what it
  // reads. Every token must appear somewhere in the row's text, so token order
  // does not matter and a partial word matches while typing.
  const results = $derived(
    tokens.length === 0
      ? rows()
      : rows().filter((row) => {
          const text = textOf(row);
          return tokens.every((token) => text.includes(token));
        }),
  );

  function cancel(): void {
    if (timer) clearTimeout(timer);
    timer = null;
  }

  $effect(() => cancel);

  return {
    get query() {
      return query;
    },
    get results() {
      return results;
    },
    get active() {
      return tokens.length > 0;
    },
    setQuery(next: string): void {
      query = next;
      cancel();
      timer = setTimeout(() => {
        timer = null;
        applied = next;
      }, DEBOUNCE_MS);
    },
    clear(): void {
      // The user asking for the whole list back is not made to wait.
      cancel();
      query = '';
      applied = '';
    },
  };
}
